import { GoogleMap, MarkerF, PolylineF, useJsApiLoader } from '@react-google-maps/api';
import type { Libraries } from '@react-google-maps/api/dist/utils/make-load-script-url';
import { useCallback, useEffect, useState } from 'react';

// Keep this identical to the shared map loader. The Google Maps loader rejects a second request with the same
// script id but a different library list.
const GOOGLE_MAPS_LIBRARIES: Libraries = ['drawing', 'geometry', 'visualization'];

const MAP_OPTIONS: google.maps.MapOptions = {
  mapTypeId: 'hybrid',
  clickableIcons: false,
  fullscreenControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  zoomControl: true,
};

export type OtsMapPosition = { lat: number; lng: number };

export type OtsMapMarker = {
  id: string;
  position: OtsMapPosition;
  /** Shown on hover, and the marker's accessible name. */
  label: string;
  color: string;
  /** Radius in pixels; 9 by default. */
  size?: number;
  /** A short label drawn on the marker, such as its place in the day. */
  text?: string;
};

export type OtsMapPath = {
  id: string;
  path: OtsMapPosition[];
  color: string;
  /** Dashed for a pairing rather than a road taken. */
  dashed?: boolean;
  weight?: number;
};

export type OtsGoogleMapProps = {
  /** The host's Google Maps key; the map explains itself without one. */
  apiKey?: string;
  markers: OtsMapMarker[];
  paths?: OtsMapPath[];
  /** What to say when there is nothing to show. */
  emptyMessage: string;
  /** What the map shows, for its messages: "this ticket", "Dana's day". */
  subject?: string;
  /** Sizes the map; a fixed height is needed. */
  className?: string;
};

/** A position worth showing: recorded, and not the (0, 0) of a missing fix. */
export const otsMapPosition = (lat?: number, lng?: number): OtsMapPosition | undefined =>
  lat != null && lng != null && (lat !== 0 || lng !== 0) ? { lat, lng } : undefined;

const Notice = ({ children, className }: { children: string; className?: string }) => (
  <div className={['flex h-24 items-center justify-center rounded-md bg-[var(--ots-surface-variant)] p-3 text-center text-xs text-[var(--ots-text-muted)]', className].filter(Boolean).join(' ')}>
    {children}
  </div>
);

const NO_PATHS: OtsMapPath[] = [];

/**
 * A satellite map of markers and paths, fitted to everything on it. It explains itself instead of failing when
 * there is nothing to show, no key, or Google Maps can't load.
 */
export const OtsGoogleMap = ({ apiKey, markers, paths = NO_PATHS, emptyMessage, subject = 'this', className }: OtsGoogleMapProps) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey ?? '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const [map, setMap] = useState<google.maps.Map>();

  const fit = useCallback(() => {
    if (!map || !globalThis.google?.maps) return;
    const positions = [...markers.map((marker) => marker.position), ...paths.flatMap((path) => path.path)];
    if (!positions.length) return;
    if (positions.length === 1) {
      map.setCenter(positions[0]);
      map.setZoom(15);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    positions.forEach((position) => bounds.extend(position));
    map.fitBounds(bounds, 40);
  }, [map, markers, paths]);
  useEffect(() => fit(), [fit]);

  if (!markers.length && !paths.some((path) => path.path.length)) return <Notice className={className}>{emptyMessage}</Notice>;
  if (!apiKey) return <Notice className={className}>{`Add a Google Maps API key to see ${subject} on the map.`}</Notice>;
  if (loadError) return <Notice className={className}>Google Maps could not be loaded.</Notice>;
  if (!isLoaded) return <Notice className={className}>Loading map…</Notice>;

  return (
    <div className={['overflow-hidden rounded-md border border-[var(--ots-outline-variant)]', className].filter(Boolean).join(' ')}>
      <GoogleMap mapContainerStyle={{ width: '100%', height: '100%' }} options={MAP_OPTIONS} onLoad={setMap} onUnmount={() => setMap(undefined)}>
        {paths.filter((path) => path.path.length >= 2).map((path) => (
          <PolylineF
            key={path.id}
            path={path.path}
            options={path.dashed
              ? {
                strokeColor: path.color,
                strokeOpacity: 0,
                icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.9, scale: 3 }, offset: '0', repeat: '14px' }],
              }
              : { strokeColor: path.color, strokeOpacity: 0.9, strokeWeight: path.weight ?? 3 }}/>
        ))}
        {markers.map((marker) => (
          <MarkerF
            key={marker.id}
            position={marker.position}
            title={marker.label}
            label={marker.text ? { text: marker.text, color: '#ffffff', fontSize: '10px', fontWeight: '700' } : undefined}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: marker.size ?? 9,
              fillColor: marker.color,
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            }}/>
        ))}
      </GoogleMap>
    </div>
  );
};
